import { useEffect, useRef, useState, useCallback } from 'react';
import { X, Camera, RefreshCcw, Video as VideoIcon, Zap, ZapOff, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_VIDEO_SECONDS = 60;

/** High-quality Instagram-style story camera for mobile.
 *
 *  - Full-screen black overlay (9:16 portrait, native camera resolution).
 *  - Tap shutter for photo, hold shutter for video (up to 60s, with timer).
 *  - Switch between front/back cameras.
 *  - Flash / torch toggle (back camera only, if supported).
 *  - Photo capture uses the video stream's native `videoWidth`/`videoHeight`
 *    (NOT the displayed size) so captures stay sharp.
 *  - Video capture uses `MediaRecorder` at up to 6 Mbps (mp4/webm).
 *  - Front camera capture is un-mirrored in the final file so text reads correctly.
 *  - After capture → preview → retake or "Post to Story".
 */
export default function StoryCameraCapture({ onClose, onPosted }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const holdTimerRef = useRef(null);
  const recordTickRef = useRef(null);

  const [facing, setFacing] = useState('environment'); // 'environment' | 'user'
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | recording | preview | uploading
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [preview, setPreview] = useState(null); // { blob, url, type: 'image'|'video', duration }
  const [initError, setInitError] = useState(null);

  /* ---------- Camera start / stop ---------- */
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      streamRef.current = null;
      trackRef.current = null;
    }
  }, []);

  const startStream = useCallback(async (mode) => {
    stopStream();
    setInitError(null);
    try {
      // Skip extra prompt friction if the permission is already granted
      // (browser remembers it for the origin — iOS Safari + Android Chrome
      //  silently honour the stored choice and we don't surface a toast).
      try {
        if (navigator.permissions?.query) {
          // best-effort; some browsers reject unknown descriptors
          await navigator.permissions.query({ name: 'camera' }).catch(() => null);
        }
      } catch {}
      const constraints = {
        audio: true, // needed for video recording with sound
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
          // advanced settings help on iOS Safari / Android Chrome
          advanced: [
            { focusMode: 'continuous' },
            { whiteBalanceMode: 'continuous' },
            { exposureMode: 'continuous' },
          ],
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      trackRef.current = track;
      // detect torch support (back cam only typically)
      const caps = typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
      setTorchSupported(!!caps?.torch && mode === 'environment');
      setTorchOn(false);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      setInitError(
        err?.name === 'NotAllowedError'
          ? 'Camera access was blocked. Please allow camera permission and try again.'
          : err?.name === 'NotFoundError'
            ? 'No camera found on this device.'
            : (err?.message || 'Camera unavailable')
      );
    }
  }, [stopStream]);

  useEffect(() => {
    startStream(facing);
    return () => {
      stopStream();
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (recordTickRef.current) clearInterval(recordTickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flipCamera = async () => {
    if (status === 'recording' || status === 'uploading') return;
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    await startStream(next);
  };

  const toggleTorch = async () => {
    if (!torchSupported || !trackRef.current) return;
    try {
      await trackRef.current.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      toast.error('Torch not available right now');
    }
  };

  /* ---------- Photo capture (sharp) ---------- */
  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return toast.error('Camera not ready');
    const w = video.videoWidth;
    const h = video.videoHeight;
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // Un-mirror front camera for the final file (preview is mirrored via CSS only)
    if (facing === 'user') {
      ctx.save();
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(video, 0, 0, w, h);
    }
    canvas.toBlob(
      (blob) => {
        if (!blob) return toast.error('Capture failed');
        const url = URL.createObjectURL(blob);
        setPreview({ blob, url, type: 'image' });
        setStatus('preview');
      },
      'image/jpeg',
      0.95, // high quality JPEG
    );
  };

  /* ---------- Video recording ---------- */
  const pickMimeType = () => {
    const candidates = [
      'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    for (const m of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
    }
    return '';
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    recordChunksRef.current = [];
    const mimeType = pickMimeType();
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 6_000_000, // 6 Mbps → crisp on mobile
        audioBitsPerSecond: 128_000,
      });
    } catch {
      try { rec = new MediaRecorder(streamRef.current); } catch { return toast.error('Recording not supported on this browser'); }
    }
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) recordChunksRef.current.push(e.data); };
    rec.onstop = () => {
      const type = (rec.mimeType || 'video/webm').split(';')[0];
      const blob = new Blob(recordChunksRef.current, { type });
      const url = URL.createObjectURL(blob);
      setPreview({ blob, url, type: 'video', duration: recordSeconds });
      setStatus('preview');
    };
    rec.start(100); // ms timeslice → more reliable chunking on iOS
    setStatus('recording');
    setRecordSeconds(0);
    recordTickRef.current = setInterval(() => {
      setRecordSeconds((s) => {
        const next = s + 1;
        if (next >= MAX_VIDEO_SECONDS) stopRecording();
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (recordTickRef.current) { clearInterval(recordTickRef.current); recordTickRef.current = null; }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch {}
    }
  };

  /* ---------- Shutter press/hold logic ---------- */
  const HOLD_MS = 260;
  const onShutterDown = (e) => {
    e.preventDefault();
    if (status !== 'idle') return;
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      startRecording();
    }, HOLD_MS);
  };
  const onShutterUp = (e) => {
    e.preventDefault();
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      if (status === 'idle') takePhoto();
      return;
    }
    if (status === 'recording') stopRecording();
  };

  /* ---------- Retake / Post ---------- */
  const retake = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setRecordSeconds(0);
    setStatus('idle');
  };

  const postToStory = async () => {
    if (!preview) return;
    setStatus('uploading');
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const fd = new FormData();
    const ext = preview.type === 'image' ? 'jpg' : (preview.blob.type.includes('mp4') ? 'mp4' : 'webm');
    const file = new File([preview.blob], `story.${ext}`, { type: preview.blob.type });
    fd.append('file', file);
    try {
      toast.loading('Posting story…', { id: 'cam-story' });
      const up = await fetch(`${API}/media/upload?category=story`, { method: 'POST', headers, body: fd });
      const upData = await up.json().catch(() => ({}));
      if (!up.ok) {
        toast.error(upData.detail || 'Upload failed', { id: 'cam-story' });
        setStatus('preview');
        return;
      }
      const url = upData.url?.startsWith('http') ? upData.url : `${process.env.REACT_APP_BACKEND_URL}${upData.url}`;
      const res = await fetch(`${API}/social/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          media_url: url,
          media_type: preview.type,
          duration_seconds: preview.type === 'video' ? preview.duration || null : null,
        }),
      });
      if (res.ok) {
        toast.success('Story posted! Lives 24h.', { id: 'cam-story' });
        URL.revokeObjectURL(preview.url);
        setPreview(null);
        stopStream();
        onPosted?.();
        onClose?.();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Could not post', { id: 'cam-story' });
        setStatus('preview');
      }
    } catch {
      toast.error('Network error', { id: 'cam-story' });
      setStatus('preview');
    }
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${m}:${ss}`;
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black flex flex-col"
      style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      data-testid="story-camera"
    >
      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-4 pt-[env(safe-area-inset-top)] pb-3 bg-gradient-to-b from-black/70 to-transparent">
        <button
          onClick={() => { stopStream(); onClose?.(); }}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
          data-testid="story-cam-close"
          aria-label="Close camera"
        >
          <X size={20} />
        </button>
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <div className={`w-2 h-2 rounded-full ${status === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-white/60'}`} />
          {status === 'recording' ? `Recording · ${fmtTime(recordSeconds)}` : 'Your Story'}
        </div>
        {status === 'idle' && torchSupported ? (
          <button
            onClick={toggleTorch}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
            data-testid="story-cam-torch"
            aria-label="Toggle flash"
          >
            {torchOn ? <Zap size={20} className="text-yellow-300" /> : <ZapOff size={20} />}
          </button>
        ) : (
          <div className="w-10 h-10" />
        )}
      </div>

      {/* Video / preview area */}
      <div className="flex-1 relative overflow-hidden bg-black">
        {initError ? (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center">
            <div>
              <p className="text-white font-bold text-lg mb-2">Camera unavailable</p>
              <p className="text-zinc-300 text-sm mb-4">{initError}</p>
              <button
                onClick={() => { stopStream(); onClose?.(); }}
                className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        ) : status === 'preview' && preview ? (
          preview.type === 'image' ? (
            <img src={preview.url} alt="Preview" className="absolute inset-0 w-full h-full object-contain" data-testid="story-cam-preview-image" />
          ) : (
            <video
              src={preview.url}
              controls
              playsInline
              autoPlay
              loop
              className="absolute inset-0 w-full h-full object-contain"
              data-testid="story-cam-preview-video"
            />
          )
        ) : (
          <video
            ref={videoRef}
            playsInline
            autoPlay
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: facing === 'user' ? 'scaleX(-1)' : undefined }}
            data-testid="story-cam-viewfinder"
          />
        )}
        {/* Record progress ring indicator */}
        {status === 'recording' && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-red-500/90 px-3 py-1 rounded-full text-white text-xs font-bold flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            REC {fmtTime(recordSeconds)} / {fmtTime(MAX_VIDEO_SECONDS)}
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div
        className="relative z-10 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] pt-5 px-6 bg-gradient-to-t from-black/80 to-transparent"
      >
        {status === 'preview' ? (
          <div className="flex items-center justify-between gap-4" data-testid="story-cam-preview-bar">
            <button
              onClick={retake}
              className="flex-1 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20"
              data-testid="story-cam-retake"
            >
              Retake
            </button>
            <button
              onClick={postToStory}
              disabled={status === 'uploading'}
              className="flex-1 h-12 rounded-full bg-gradient-to-r from-fuchsia-500 via-pink-500 to-amber-400 text-black font-bold text-sm flex items-center justify-center gap-2 shadow-[0_10px_30px_-8px_rgba(236,72,153,0.6)] disabled:opacity-60"
              data-testid="story-cam-post"
            >
              {status === 'uploading' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {status === 'uploading' ? 'Posting…' : 'Post to Story'}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            {/* mode hint */}
            <div className="w-14 text-white/70 text-[11px] font-semibold">
              {status === 'recording' ? 'Release to stop' : 'Tap · Photo\nHold · Video'}
            </div>
            {/* Shutter */}
            <button
              onPointerDown={onShutterDown}
              onPointerUp={onShutterUp}
              onPointerCancel={onShutterUp}
              onPointerLeave={status === 'recording' ? undefined : onShutterUp}
              className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all ${status === 'recording' ? 'scale-105' : 'active:scale-95'}`}
              data-testid="story-cam-shutter"
              aria-label="Capture"
            >
              <span className={`absolute inset-0 rounded-full border-4 ${status === 'recording' ? 'border-red-400' : 'border-white'}`} />
              <span className={`rounded-full transition-all ${status === 'recording' ? 'w-8 h-8 bg-red-500 rounded-lg' : 'w-16 h-16 bg-white'}`} />
              {status === 'recording' && (
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50" cy="50" r="46"
                    fill="none"
                    stroke="#f43f5e"
                    strokeWidth="4"
                    strokeDasharray={`${(recordSeconds / MAX_VIDEO_SECONDS) * 289} 289`}
                  />
                </svg>
              )}
            </button>
            {/* Flip */}
            <button
              onClick={flipCamera}
              disabled={status === 'recording'}
              className="w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-40"
              data-testid="story-cam-flip"
              aria-label="Flip camera"
            >
              <RefreshCcw size={22} />
            </button>
          </div>
        )}
        {status !== 'preview' && status !== 'recording' && (
          <div className="mt-3 flex items-center justify-center gap-2 text-white/60 text-[11px]">
            <Camera size={12} /> Tap for photo · <VideoIcon size={12} /> Hold for video (up to {MAX_VIDEO_SECONDS}s)
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
